/**
 * BankReconciliationTab — CON-06: Conciliación Bancaria
 * Lista de reconciliaciones + detalle con cartola y líneas del sistema.
 */
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import DatePicker from '@/components/DatePicker'
import { fmtDate } from '@/lib/utils'
import { useTenantLocale } from '@/lib/locale'
import { Plus, Loader2, ArrowLeft, Upload, Zap, CheckCircle2, AlertCircle, Link2, Landmark } from 'lucide-react'
import {
  useBankReconciliations, useBankReconciliation,
  useCreateReconciliation,
  useAutoReconcile, useManualMatch, useCloseReconciliation,
  useAccounts,
  type BankReconciliation, type BankStatementLine, type SystemLine,
} from '../hooks/useAccounting'
import { BankStatementUploadModal } from './BankStatementUploadModal'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

// ─── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  in_progress: { label: 'En progreso', className: 'bg-primary-50 text-primary-hover border-primary-200' },
  closed:      { label: 'Cerrada',     className: 'bg-success-surface text-success-content border-success-border' },
} as const

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? { label: status, className: 'bg-surface-muted text-content-muted border-divider' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

// ─── Create reconciliation modal ───────────────────────────────────────────────

function CreateReconciliationModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (data: { bank_account: string; period_start: string; period_end: string }) => void
}) {
  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const accounts = accountsData?.accounts ?? []

  const today = todayISO()
  const firstOfMonth = today.substring(0, 7) + '-01'

  const [bankAccount, setBankAccount] = useState('')
  const [periodStart, setPeriodStart] = useState(firstOfMonth)
  const [periodEnd, setPeriodEnd] = useState(today)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!bankAccount) return
    onCreate({ bank_account: bankAccount, period_start: periodStart, period_end: periodEnd })
  }

  return (
    <Modal open onClose={onClose} title="Nueva Conciliación Bancaria" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="bankreconciliationtab-cuenta-bancaria" className="block text-xs font-medium text-content-muted mb-1">Cuenta Bancaria *</label>
          <select id="bankreconciliationtab-cuenta-bancaria"
            value={bankAccount}
            onChange={e => setBankAccount(e.target.value)}
            required
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="">Seleccionar cuenta...</option>
            {accounts.filter(a => a.account_type === 'asset').map(a => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="bankreconciliationtab-periodo-inicio" className="block text-xs font-medium text-content-muted mb-1">Período Inicio *</label>
            <DatePicker id="bankreconciliationtab-periodo-inicio" value={periodStart} onChange={setPeriodStart} required />
          </div>
          <div>
            <label htmlFor="bankreconciliationtab-periodo-fin" className="block text-xs font-medium text-content-muted mb-1">Período Fin *</label>
            <DatePicker id="bankreconciliationtab-periodo-fin" value={periodEnd} onChange={setPeriodEnd} required />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-divider rounded-lg text-sm text-content-muted hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={!bankAccount}
            className="flex-1 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover disabled:opacity-50">
            Crear
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Reconciliation detail ─────────────────────────────────────────────────────

function ReconciliationDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const locale = useTenantLocale()
  const { data, isLoading, refetch } = useBankReconciliation(id)
  const autoReconcile = useAutoReconcile()
  const manualMatch = useManualMatch()
  const closeRec = useCloseReconciliation()

  const [selectedSystemLineId, setSelectedSystemLineId] = useState<string | null>(null)
  const [selectedStatementLineId, setSelectedStatementLineId] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)

  const handleAutoReconcile = async () => {
    if (!data) return
    await autoReconcile.mutateAsync(id)
    await refetch()
  }

  const handleMatch = async () => {
    if (!selectedSystemLineId || !selectedStatementLineId || !data) return
    await manualMatch.mutateAsync({
      id,
      voucherLineId: selectedSystemLineId,
      statementLineId: selectedStatementLineId,
    })
    setSelectedSystemLineId(null)
    setSelectedStatementLineId(null)
    await refetch()
  }

  const handleClose = async () => {
    if (!data) return
    await closeRec.mutateAsync(id)
    await refetch()
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16 text-content-subtle">
        <Loader2 size={20} className="animate-spin mr-2" />Cargando...
      </div>
    )
  }

  const isClosed = data.status === 'closed'
  const diff = parseFloat(data.difference)
  const canClose = !isClosed && diff === 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-soft text-content-muted transition-colors">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-content">
              Conciliación {fmtDate(data.period_start, locale)} — {fmtDate(data.period_end, locale)}
            </h2>
            <StatusBadge status={data.status} />
          </div>
          <p className="text-xs text-content-muted mt-0.5">
            Cuenta: {data.bank_account_name ?? data.bank_account}
            {data.bank_account_currency && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-xs font-mono bg-warning-surface text-warning-content rounded border border-warning-border">
                {data.bank_account_currency}
              </span>
            )}
            {' · '}
            Diferencia:{' '}
            <span className={Math.abs(diff) < 0.01 ? 'text-success-content-soft font-medium' : 'text-danger-content-soft font-medium'}>
              {diff.toFixed(2)}{data.bank_account_currency ? ` ${data.bank_account_currency}` : ''}
            </span>
          </p>
        </div>
        {!isClosed && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-divider rounded-lg text-xs text-content-muted hover:bg-soft"
            >
              <Upload size={13} />
              Subir Cartola CSV
            </button>
            <button
              onClick={handleAutoReconcile}
              disabled={autoReconcile.isPending || !data.statement_lines?.length}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-divider rounded-lg text-xs text-content-muted hover:bg-soft disabled:opacity-50"
            >
              <Zap size={13} />
              Auto-conciliar
            </button>
            {selectedSystemLineId && selectedStatementLineId && (
              <button
                onClick={handleMatch}
                disabled={manualMatch.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700"
              >
                <Link2 size={13} />
                Emparejar
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={!canClose || closeRec.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-fill text-white rounded-lg text-xs hover:bg-success-fill-hover disabled:opacity-50"
            >
              <CheckCircle2 size={13} />
              Cerrar Conciliación
            </button>
          </div>
        )}
      </div>

      {/* Split panel */}
      <div className="grid grid-cols-2 gap-4">
        {/* System lines */}
        <div>
          <h3 className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">
            Líneas del Sistema ({data.system_lines?.length ?? 0})
          </h3>
          <DataTable<SystemLine>
            columns={[
              { key: 'date', header: 'Fecha', sortable: true,
                render: l => <span className="text-content-muted">{fmtDate(l.date, locale)}</span> },
              { key: 'voucher_number', header: 'Comprobante', sortable: true,
                render: l => <span className="font-mono text-content">{l.voucher_number}</span> },
              { key: 'net', numeric: true, sortable: true,
                header: `Neto${data.bank_account_currency ? ` (${data.bank_account_currency})` : ''}`,
                sortValue: l => Number(l.net),
                render: l => (
                  <span className="font-medium text-content">
                    {parseFloat(l.net).toLocaleString('es-CL', {
                      maximumFractionDigits: data.bank_account_currency ? 2 : 0,
                      minimumFractionDigits: data.bank_account_currency ? 2 : 0,
                    })}
                  </span>
                ) },
              { key: 'matched', header: 'Estado', sortable: true,
                sortValue: l => (l.matched ? 1 : 0),
                render: l => (!l.matched
                  ? <span className="text-warning-content-soft">Pendiente</span>
                  : <CheckCircle2 size={13} className="inline text-success-solid" />) },
            ]}
            rows={data.system_lines ?? []}
            rowKey={l => l.id}
            minWidth={380}
            // Una línea ya conciliada no se selecciona: sin esto quedaría con
            // cursor de mano prometiendo una acción que no ocurre.
            rowClickable={l => !isClosed && !l.matched}
            onRowClick={l => setSelectedSystemLineId(selectedSystemLineId === l.id ? null : l.id)}
            selectedKey={selectedSystemLineId ?? undefined}
            empty={<EmptyState title="Sin líneas en el período" size="sm" />}
            caption="Movimientos contables de la cuenta en el período"
          />
        </div>

        {/* Statement lines */}
        <div>
          <h3 className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">
            Cartola Bancaria ({data.statement_lines?.length ?? 0})
          </h3>
          <DataTable<BankStatementLine>
            columns={[
              { key: 'transaction_date', header: 'Fecha', sortable: true,
                render: l => <span className="text-content-muted">{fmtDate(l.transaction_date, locale)}</span> },
              { key: 'description', header: 'Descripción', sortable: true,
                render: l => (
                  <span className="text-content-muted max-w-[120px] truncate block">{l.description}</span>
                ) },
              { key: 'amount', header: 'Monto', numeric: true, sortable: true,
                sortValue: l => Number(l.amount),
                render: l => {
                  const amount = parseFloat(l.amount)
                  return (
                    <span className={`font-medium ${amount >= 0 ? 'text-success-content' : 'text-danger-content-soft'}`}>
                      {amount.toLocaleString('es-CL', {
                        maximumFractionDigits: data.bank_account_currency ? 2 : 0,
                        minimumFractionDigits: data.bank_account_currency ? 2 : 0,
                      })}
                    </span>
                  )
                } },
              { key: 'status', header: 'Estado', sortable: true,
                render: l => (l.status === 'pending' ? (
                  <span className="text-warning-content-soft">Pendiente</span>
                ) : l.status === 'reconciled_auto' ? (
                  <span className="inline-flex items-center gap-0.5 text-success-content-soft">
                    <Zap size={11} />Auto
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 text-indigo-600 dark:text-indigo-300">
                    <Link2 size={11} />Manual
                  </span>
                )) },
            ]}
            rows={data.statement_lines ?? []}
            rowKey={l => l.id}
            minWidth={380}
            rowClickable={l => !isClosed && l.status === 'pending'}
            onRowClick={l => setSelectedStatementLineId(selectedStatementLineId === l.id ? null : l.id)}
            selectedKey={selectedStatementLineId ?? undefined}
            empty={<EmptyState title="Sube una cartola CSV para comenzar" size="sm" />}
            caption="Movimientos de la cartola del banco"
          />
        </div>
      </div>

      {/* Match hint */}
      {(selectedSystemLineId || selectedStatementLineId) && (
        <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-100 text-xs text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
          <AlertCircle size={14} />
          {selectedSystemLineId && !selectedStatementLineId && 'Seleccionar una línea de la cartola para emparejar.'}
          {!selectedSystemLineId && selectedStatementLineId && 'Seleccionar una línea del sistema para emparejar.'}
          {selectedSystemLineId && selectedStatementLineId && 'Hacer clic en "Emparejar" para confirmar la conciliación manual.'}
        </div>
      )}

      <BankStatementUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        reconciliationId={id}
        onSuccess={() => { refetch() }}
      />
    </div>
  )
}

// ─── Main Tab ──────────────────────────────────────────────────────────────────

export function BankReconciliationTab() {
  const locale = useTenantLocale()
  const [showCreate, setShowCreate] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useBankReconciliations()
  const create = useCreateReconciliation()

  const reconciliations = data?.reconciliations ?? []

  const handleCreate = async (formData: { bank_account: string; period_start: string; period_end: string }) => {
    const rec = await create.mutateAsync(formData)
    setShowCreate(false)
    setSelectedId(rec.id)
  }

  if (selectedId) {
    return <ReconciliationDetail id={selectedId} onBack={() => setSelectedId(null)} />
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-content-muted">
          {data ? `${data.count} conciliación${data.count !== 1 ? 'es' : ''}` : ''}
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
        >
          <Plus size={14} />
          Nueva Conciliación
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando...
        </div>
      ) : reconciliations.length === 0 ? (
        <EmptyState
          icon={Landmark}
          title="No hay conciliaciones bancarias."
          hint="Crea una nueva para comenzar."
          size="md"
        />
      ) : (
        <DataTable<BankReconciliation>
          columns={[
            { key: 'bank_account_name', header: 'Cuenta', sortable: true,
              sortValue: rec => rec.bank_account_name ?? rec.bank_account,
              render: rec => (
                <span className="text-content">
                  {rec.bank_account_name ?? rec.bank_account}
                  {rec.bank_account_currency && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-xs font-mono bg-warning-surface text-warning-content rounded border border-warning-border">
                      {rec.bank_account_currency}
                    </span>
                  )}
                </span>
              ) },
            { key: 'period_start', header: 'Período', sortable: true,
              render: rec => (
                <span className="text-content-muted">
                  {fmtDate(rec.period_start, locale)} — {fmtDate(rec.period_end, locale)}
                </span>
              ) },
            { key: 'difference', header: 'Diferencia', numeric: true, sortable: true,
              sortValue: rec => Number(rec.difference),
              render: rec => {
                const diff = parseFloat(rec.difference)
                return (
                  <span className={`font-medium ${Math.abs(diff) < 0.01 ? 'text-success-content-soft' : 'text-danger-content-soft'}`}>
                    {diff.toFixed(2)}
                  </span>
                )
              } },
            { key: 'status', header: 'Estado', sortable: true,
              render: rec => <StatusBadge status={rec.status} /> },
            { key: 'acciones', header: '', width: 110,
              render: rec => (
                <button
                  onClick={() => setSelectedId(rec.id)}
                  className="text-xs text-primary hover:text-primary-hover font-medium"
                >
                  Ver detalle →
                </button>
              ) },
          ]}
          rows={reconciliations}
          rowKey={rec => rec.id}
          caption="Conciliaciones bancarias registradas"
        />
      )}

      {showCreate && (
        <CreateReconciliationModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  )
}
